import api from './api';

export interface SubParishDto {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  sectorAccessLevel?: 'leitura' | 'eventos' | 'operacao';
  city?: string;
  state?: string;
  address?: string;
  phone?: string;
  email?: string;
  active?: boolean;
  createdAt?: string;
}

export interface ParishProfileDto {
  _id: string;
  name: string;
  slug: string;
  city: string;
  state: string;
  address?: string;
  streetNumber?: string;
  neighborhood?: string;
  ibgeMunicipioCode?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  description?: string;
  logoUrl?: string;
  isMainParish: boolean;
}

export interface MyParishesResponse {
  parish: ParishProfileDto;
  subParishes: SubParishDto[];
  sectorAccess?: {
    path: string;
    max: number | null;
    used: number;
  };
}

interface ParishData {
  name: string;
  address?: string;
  streetNumber?: string;
  neighborhood?: string;
  ibgeMunicipioCode?: string;
  city: string;
  state: string;
  zipCode?: string;
  parishEmail?: string;
  description?: string;
  cnpj: string;
}

/** Rascunho de recebedor PJ enviado no cadastro público (recebimentos / repasses). */
export interface PublicRecipientProfilePayload {
  registerType: 'corporation';
  corporation: {
    companyName: string;
    tradingName: string;
    annualRevenue: string;
    foundingDate?: string;
    representative: {
      name: string;
      email: string;
      document: string;
      birthdate: string;
      phone?: string;
      selfDeclaredRepresentative: boolean;
    };
  };
  bankAccount: {
    holderName: string;
    holderDocument: string;
    bank: string;
    branchNumber: string;
    branchCheckDigit?: string;
    accountNumber: string;
    accountCheckDigit: string;
    type: 'checking' | 'savings';
  };
}

interface ParishRequestPayload {
  name: string;
  email: string;
  phone: string;
  signerCpf: string;
  parishData: ParishData;
  termsAccepted: boolean;
  signatureName?: string;
  contractViewed?: boolean;
  legalAccepted?: { key: string; version: string }[];
  recipientProfile: PublicRecipientProfilePayload;
  customPageInviteToken?: string;
}

// Estrutura padrão de resposta
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

interface DuplicateCheckResponse {
  email: boolean;
  signerCpf: boolean;
  parishCnpj: boolean;
  parishName: boolean;
}

export const parishService = {
  heckNameExists: async (name: string): Promise<boolean> => {
    const response = await api.get<ApiResponse<{ exists: boolean }>>('/auth/check-parish-name', {
      params: { name }
    });

    return response.data.data?.exists ?? false;
  },

  checkDuplicates: async (
    email?: string,
    signerCpf?: string,
    parishCnpj?: string,
    parishName?: string
  ): Promise<DuplicateCheckResponse> => {
    const params: Record<string, string> = {};
    if (email) params.email = email;
    if (signerCpf) params.signerCpf = signerCpf;
    if (parishCnpj) params.parishCnpj = parishCnpj;
    if (parishName) params.parishName = parishName;

    const response = await api.get<
      ApiResponse<
        DuplicateCheckResponse & {
          cpfCnpj?: boolean;
        }
      >
    >('/public/parishes/check-duplicates', {
      params,
    });

    const d =
      response.data.data ||
      ({
        email: false,
        signerCpf: false,
        parishCnpj: false,
        parishName: false,
      } as DuplicateCheckResponse & { cpfCnpj?: boolean });

    const signerDup = !!(d.signerCpf ?? d.cpfCnpj);

    return {
      email: !!d.email,
      signerCpf: signerDup,
      parishCnpj: !!d.parishCnpj,
      parishName: !!d.parishName,
    };
  },

  requestParishRegistration: async (payload: ParishRequestPayload): Promise<void> => {
    const host = typeof window !== 'undefined' ? window.location.hostname : undefined;
    await api.post<ApiResponse<null>>(
      '/public/parishes/request',
      host ? { ...payload, host } : payload,
      { params: host ? { host } : undefined }
    );
  },

  /** Paróquia do usuário + subparóquias (se for paróquia principal) */
  getMyParishes: async (): Promise<MyParishesResponse> => {
    const response = await api.get<ApiResponse<MyParishesResponse>>('/parishes/me');
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Erro ao carregar paróquias');
    return response.data.data;
  },

  updateMyParish: async (data: Partial<{
    name: string;
    address: string;
    streetNumber: string;
    neighborhood: string;
    ibgeMunicipioCode: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    email: string;
    description: string;
  }>) => {
    const response = await api.put<ApiResponse<ParishProfileDto>>('/parishes/me', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Erro ao atualizar paróquia');
    }
    return response.data.data;
  },

  updateMyParishLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.patch<ApiResponse<ParishProfileDto>>('/parishes/me/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Erro ao atualizar logo');
    }
    return response.data.data;
  },

  /** Cria acesso de setor (subparóquia). Obrigatório: name, password, description. */
  createSubParish: async (data: {
    name: string;
    password: string;
    description: string;
    sectorAccessLevel?: 'leitura' | 'eventos' | 'operacao';
  }) => {
    const response = await api.post<ApiResponse<SubParishDto>>('/parishes/sub-parishes', data);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao criar subparóquia');
    return response.data.data!;
  },

  updateSubParish: async (id: string, data: Partial<{
    name: string;
    description: string;
    sectorAccessLevel: 'leitura' | 'eventos' | 'operacao';
    active: boolean;
    password: string;
  }>) => {
    const response = await api.put<ApiResponse<SubParishDto>>(`/parishes/sub-parishes/${id}`, data);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao atualizar subparóquia');
    return response.data.data!;
  },

  /** Exclui (desativa) uma subparóquia. O usuário admin da subparóquia deixa de acessar. */
  deleteSubParish: async (id: string) => {
    const response = await api.delete<ApiResponse<null>>(`/parishes/sub-parishes/${id}`);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao excluir subparóquia');
  },
};